import { App } from '@afinius/api-client/landlord-application/types';

import { isCapacitor } from './mobile';

const isProxy = import.meta.env.MODE === 'proxy';

const localOrigins = {
  [App.ApiTenant]: import.meta.env.VITE_API_TENANT_BASE_URL,
  [App.ApiLandlord]: import.meta.env.VITE_API_BASE_URL,
  [App.AfiniusAuth]: 'http://localhost:8080',
  [App.AfiniusPortal]: 'http://localhost:8081',
  [App.CityviewOverview]: 'http://localhost:8084',
  [App.Cityview]: 'http://localhost:8082',
  [App.Nodr]: 'http://localhost:8083',
  [App.CitizenReport]: 'http://localhost:8085',
  [App.SensorRegistry]: 'http://localhost:8086',
};

const proxyDomains = {
  [App.ApiTenant]: new URL(import.meta.env.VITE_API_BASE_URL).hostname,
  [App.ApiLandlord]: new URL(import.meta.env.VITE_API_BASE_URL).hostname,
  [App.AfiniusAuth]: 'auth.mijnafinius.test',
  [App.AfiniusPortal]: 'mijnafinius.test',
  [App.CityviewOverview]: 'overzicht.cityview.test',
  [App.Cityview]: 'cityview.test',
  [App.Nodr]: 'mijnnodr.test',
  [App.CitizenReport]: 'doeeenmelding.test',
  [App.SensorRegistry]: 'sensorregister.test',
};

const productionDomains = {
  [App.ApiTenant]: 'api.mijnafinius.nl',
  [App.ApiLandlord]: 'api.mijnafinius.nl',
  [App.AfiniusAuth]: 'auth.mijnafinius.nl',
  [App.AfiniusPortal]: 'mijnafinius.nl',
  [App.CityviewOverview]: 'overzicht.cityview.nl',
  [App.Cityview]: 'cityview.nl',
  [App.Nodr]: 'mijnnodr.nl',
  [App.CitizenReport]: 'doeeenmelding.nl',
  [App.SensorRegistry]: 'sensorregister.nl',
};

const tenantSpecific = {
  [App.ApiTenant]: true,
  [App.ApiLandlord]: false,
  [App.AfiniusAuth]: false,
  [App.AfiniusPortal]: true,
  [App.CityviewOverview]: false,
  [App.Cityview]: true,
  [App.Nodr]: true,
  [App.CitizenReport]: true,
  [App.SensorRegistry]: true,
};

export const currentApp = import.meta.env.VITE_API_APPLICATION_KEY as App;

export const isApp = (key: string): key is App => Object.values<string>(App).includes(key);

const getAppOrigin = (targetApp: App, tenantDomain?: string) => {
  const hostname = isCapacitor
    ? new URL(import.meta.env.VITE_API_BASE_URL).hostname
    : location.hostname;

  if (hostname === 'localhost') {
    return localOrigins[targetApp];
  }

  const domains = isProxy ? proxyDomains : productionDomains;

  const domain = tenantDomain || hostname;
  const currentDomain = Object.values(domains).find((value) => domain.endsWith(value)) ?? domain;
  const currentApp = Object.values(App).find((key) => domains[key] === currentDomain) as App;
  const targetDomain = domains[targetApp];

  if (tenantSpecific[targetApp] === tenantSpecific[currentApp]) {
    return `https://${domain.replace(currentDomain, targetDomain)}`;
  }

  return `https://${domain.replace(currentDomain, targetDomain).split('.').slice(1).join('.')}`;
};

export const getAppUrl = (app: App, path?: string, tenantDomain?: string) => {
  const origin = getAppOrigin(app, tenantDomain);
  return path ? `${origin}/${path}` : origin;
};

export const redirectToApp = (app: App, path?: string, tenantDomain?: string) => {
  window.location.href = getAppUrl(app, path, tenantDomain);
};

export const getTenantUrl = (tenantDomain: string) => {
  return getAppUrl(currentApp, undefined, tenantDomain);
};
